import { Injectable } from '@angular/core';

import { Store, select } from '@ngrx/store';
import { Observable, combineLatest, of } from 'rxjs';
import { first, switchMap, map, take } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import { CurrencyService } from '@fcom/core';
import { snapshot } from '@fcom/rx';

import { PaymentTotal } from '../interfaces/payment-total.interface';
import { calculatePaymentTotal, getBuyerVouchersCount, isLongHaulUpgraded } from '../utils/payment.utils';
import { State } from '../interfaces/store.interface';
import { PaymentType } from '../interfaces/payment-type.interface';
import { ExtendedFlightBasket } from '../interfaces/upgrades.interface';
import { VoucherType, FlightsV2, FlightV2, UpgradeProduct, PaymentOptionsV2, Buyer } from '../api/models';
import { getBasketPassengerIds, getBasketFlights, getPaymentTotal, getFlights, getUser } from '../store/selectors';
import {
  getCabinFromUpgrade,
  getPaymentFromFlight,
  getUpgradeFromFlight,
  getVoucherTypeFromPaymentType,
} from '../utils/data.utils';

export const sortByPaymentType = (a: PaymentType, b: PaymentType): number =>
  a !== PaymentType.money && b === PaymentType.money ? -1 : 1;

@Injectable({
  providedIn: 'root',
})
export class UpgradePaymentService {
  flights$: Observable<FlightsV2>;
  selectedPassengers$: Observable<string[]>;
  selectedFlights$: Observable<ExtendedFlightBasket[]>;
  paymentTotal$: Observable<PaymentTotal>;
  buyer$: Observable<Buyer>;

  constructor(
    private store: Store<State>,
    private languageService: LanguageService,
    private currencyService: CurrencyService
  ) {
    this.flights$ = this.store.pipe(select(getFlights));
    this.selectedPassengers$ = this.store.pipe(select(getBasketPassengerIds));
    this.selectedFlights$ = this.store.pipe(select(getBasketFlights));
    this.paymentTotal$ = this.store.pipe(select(getPaymentTotal));
    this.buyer$ = this.store.pipe(select(getUser));
  }

  /**
   * get the formated price for a given flight in the basket
   *
   * @param flight flight to Upgrade
   */
  getPriceForSelectedFlight(flight: FlightV2): Observable<string> {
    const id = flight.segmentInfo.flightId;

    return combineLatest([this.selectedFlights$, this.selectedPassengers$]).pipe(
      first(),
      switchMap(([flightsBasket, selectedPassengers]) => {
        const passengerAmount = selectedPassengers.length;
        const flightBasket: ExtendedFlightBasket = flightsBasket.find((selected) => selected.flightId === id);
        const shortHaulDiscount =
          isLongHaulUpgraded(flightsBasket, flight.segmentInfo.group) && !flight.segmentInfo.longHaul;

        return this.getFormattedPaymentAmount(
          flight,
          flightBasket.productName,
          flightBasket.paymentType,
          passengerAmount,
          shortHaulDiscount
        );
      })
    );
  }

  getValueForPaymentOption(flight: FlightV2, upgrade: UpgradeProduct, paymentType: PaymentType): Observable<string> {
    return combineLatest([this.selectedFlights$, this.selectedPassengers$]).pipe(
      first(),
      switchMap(([flightsBasket, selectedPassengers]) => {
        const passengerAmount = selectedPassengers.length;
        const shortHaulDiscount =
          isLongHaulUpgraded(flightsBasket, flight.segmentInfo.group) && !flight.segmentInfo.longHaul;

        return this.getFormattedPaymentAmount(flight, upgrade, paymentType, passengerAmount, shortHaulDiscount);
      })
    );
  }

  getAllSelectedPayments(): Observable<string[]> {
    return this.paymentTotal$.pipe(
      switchMap((totalPrice: PaymentTotal) => {
        return combineLatest([
          this.getFormattedMoney(totalPrice.money, totalPrice.currency),
          this.getFormattedPoints(totalPrice.points, false),
          this.getFormattedVouchers(totalPrice[PaymentType.EUROPE_UPGRADE], VoucherType.EuropeUpgrade),
          this.getFormattedVouchers(totalPrice[PaymentType.LONG_HAUL_UPGRADE], VoucherType.LongHaulUpgrade),
          this.getFormattedVouchers(totalPrice[PaymentType.UPGRADE_BENEFIT], VoucherType.UpgradeBenefit),
        ]);
      }),
      map((labels) => labels.filter((label) => label))
    );
  }

  getFormattedPaymentAmount(
    flight: FlightV2,
    productName: UpgradeProduct,
    paymentType: PaymentType,
    passengerAmount: number,
    shortHaulDiscount: boolean
  ): Observable<string> {
    const paymentOption = getPaymentFromFlight(flight, productName, paymentType);

    switch (paymentType) {
      case PaymentType.money: {
        const payment = paymentOption as PaymentOptionsV2['money'];

        return this.getFormattedMoney(parseFloat(payment.price) * passengerAmount, payment.currency);
      }
      case PaymentType.points: {
        const payment = paymentOption as PaymentOptionsV2['points'];

        return shortHaulDiscount && payment.priceWithLongHaul !== undefined
          ? this.getFormattedPoints(passengerAmount * payment.priceWithLongHaul)
          : this.getFormattedPoints(passengerAmount * payment.price);
      }
      default: {
        const payment = paymentOption as PaymentOptionsV2['EUROPE_UPGRADE'];
        const totalVouchers = passengerAmount * payment.price;
        return this.getFormattedVouchers(totalVouchers, getVoucherTypeFromPaymentType(paymentType));
      }
    }
  }

  private getFormattedVouchers(amount: number, voucherType: VoucherType): Observable<string> {
    const type = amount > 1 ? 'plural' : 'singular';
    const translationKey = `oneUpgrade.vouchers.${voucherType}.${type}`;

    return amount ? this.languageService.translate(translationKey, { voucherAmount: amount }).pipe(first()) : of('');
  }

  private getFormattedMoney(moneyPrice: number, currency: string): Observable<string> {
    return moneyPrice
      ? this.currencyService.formatPrice({ amount: moneyPrice.toString(), currencyCode: currency }).pipe(first())
      : of('');
  }

  // TODO: implement pipe, check asian formatting
  /**
   * Format points as string
   *
   * @param pointsAmount number of points
   * @param allowZero  boolean indicating if to translate 0 points or return blank
   */
  private getFormattedPoints(pointsAmount: number, allowZero = true): Observable<string> {
    if (pointsAmount) {
      return this.languageService.translate('number').pipe(
        switchMap((i18n) => {
          const formattedPoints = pointsAmount
            .toString()
            .replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${i18n.thousandSeparator}`);

          return this.languageService.translate('oneUpgrade.points', { pointsAmount: formattedPoints });
        }),
        first()
      );
    }

    return allowZero ? this.languageService.translate('oneUpgrade.points', { pointsAmount: 0 }) : of('');
  }

  /**
   * Does the buyer have sufficient payment to be able to select this payment option
   * @param flight Current flight details
   * @param productName What kind of upgrade e.g. economy to business
   * @param paymentType The current payment option to check
   */
  insufficientPayment(flight: FlightV2, productName: UpgradeProduct, paymentType: PaymentType): boolean {
    const isInsufficient$ = combineLatest([this.selectedFlights$, this.selectedPassengers$, this.buyer$]).pipe(
      take(1),
      map(([flightsBasket, selectedPassengers, buyer]) => {
        if (paymentType === PaymentType.money) {
          return false;
        }

        if (!buyer) {
          return true;
        }

        const paymentTotal = calculatePaymentTotal(
          [
            ...flightsBasket,
            {
              flight,
              flightId: flight.segmentInfo.flightId,
              productName,
              paymentType,
              cabin: getCabinFromUpgrade(getUpgradeFromFlight(flight, productName)),
            },
          ],
          selectedPassengers.length,
          buyer
        );

        return (
          paymentTotal.points > (buyer.points || 0) ||
          paymentTotal[PaymentType.EUROPE_UPGRADE] > getBuyerVouchersCount(buyer, VoucherType.EuropeUpgrade) ||
          paymentTotal[PaymentType.LONG_HAUL_UPGRADE] > getBuyerVouchersCount(buyer, VoucherType.LongHaulUpgrade) ||
          paymentTotal[PaymentType.UPGRADE_BENEFIT] > getBuyerVouchersCount(buyer, VoucherType.UpgradeBenefit)
        );
      })
    );
    return snapshot(isInsufficient$);
  }
}
